import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ShoppingCart, ArrowLeft, Loader2, CheckCircle, XCircle, CreditCard } from 'lucide-react';
import SystemPayForm from '../components/SystemPayForm';
import { redirectToSystemPay } from '../lib/systempayRedirect';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PAYMENTS_ENABLED = process.env.REACT_APP_PAYMENTS_ENABLED !== 'false';

export default function CheckoutPage() {
  const { user, token, loading: authLoading } = useAuth();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [formToken, setFormToken] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { toast.error('Veuillez vous connecter'); navigate('/connexion'); return; }
    if (cartItems.length === 0 && !paymentStatus && !formToken) navigate('/boutique');
  }, [authLoading, user, cartItems, navigate, paymentStatus, formToken]);

  const startPayment = async () => {
    setLoading(true);
    try {
      const items = cartItems.map(item => ({
        description: item.name, quantity: item.quantity || 1,
        unit_price: item.price, total: item.price * (item.quantity || 1)
      }));
      const res = await fetch(`${API}/payment/shop/checkout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, total: getCartTotal(), origin_url: window.location.origin })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Erreur'); }
      const data = await res.json();
      setOrderId(data.orderId);

      // Mode paiement desactive : la commande est creee directement, on saute le formulaire
      if (data.skip_payment) {
        clearCart();
        setPaymentStatus('success');
        toast.success('Commande enregistree ! A regler en magasin.');
        return;
      }

      // Mode redirection SystemPay (recommande) : on quitte le site
      if (data.redirect_form) {
        redirectToSystemPay(data.redirect_form);
        return;
      }

      setFormToken(data.formToken);
    } catch (error) {
      toast.error(error.message || 'Erreur');
    } finally { setLoading(false); }
  };

  const handlePaid = useCallback(async () => {
    setPaymentStatus('success');
    try {
      await fetch(`${API}/payment/shop/confirm?order_id=${orderId}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { console.error(e); }
    clearCart();
    toast.success('Paiement reussi !');
  }, [orderId, token, clearCart]);

  const handleFailed = useCallback(() => {
    setPaymentStatus('error');
  }, []);

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
        <Card className="bg-zinc-900/50 border-zinc-800 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
            <h2 className="text-2xl font-bold text-white mb-2">
              {PAYMENTS_ENABLED ? 'Paiement reussi !' : 'Commande enregistree !'}
            </h2>
            <p className="text-zinc-400 mb-6">
              {PAYMENTS_ENABLED
                ? 'Votre commande a ete confirmee.'
                : 'Le paiement se fera en magasin lors du retrait. Nous vous contacterons rapidement.'}
            </p>
            <Button onClick={() => navigate('/mon-compte')} className="bg-red-600 hover:bg-red-700 w-full" data-testid="checkout-success-account">
              Voir mes commandes
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentStatus === 'error') {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
        <Card className="bg-zinc-900/50 border-zinc-800 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="mx-auto text-red-500 mb-4" size={64} />
            <h2 className="text-2xl font-bold text-white mb-2">Echec du paiement</h2>
            <p className="text-zinc-400 mb-6">Veuillez reessayer.</p>
            <Button
              onClick={() => { setPaymentStatus(null); setFormToken(null); setOrderId(null); }}
              variant="outline" className="border-zinc-700 w-full" data-testid="checkout-retry-btn">
              Reessayer
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] py-12">
      <div className="max-w-2xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/boutique')} className="text-zinc-400 mb-6" data-testid="checkout-back-btn">
          <ArrowLeft size={18} className="mr-2" /> Retour a la boutique
        </Button>
        <h1 className="text-3xl font-bold text-white mb-8">
          <ShoppingCart className="inline mr-3 text-red-500" size={32} /> PAIEMENT
        </h1>

        <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
          <CardHeader><CardTitle className="text-white">Recapitulatif</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {cartItems.map((item) => (
                <div key={item.id} className="flex justify-between items-center py-2 border-b border-zinc-800">
                  <div className="flex items-center gap-3">
                    <img src={item.image_url} alt={item.name} className="w-12 h-12 rounded object-cover" />
                    <div>
                      <p className="text-white font-medium">{item.name}</p>
                      <p className="text-zinc-500 text-sm">x{item.quantity || 1}</p>
                    </div>
                  </div>
                  <p className="text-white font-bold">{(item.price * (item.quantity || 1)).toFixed(2)} EUR</p>
                </div>
              ))}
              <div className="flex justify-between items-center pt-3 border-t border-zinc-700">
                <p className="text-lg text-white font-bold">Total</p>
                <p className="text-2xl text-red-500 font-bold" data-testid="checkout-total">{getCartTotal().toFixed(2)} EUR</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="text-red-500" size={20} /> Paiement securise
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="py-4">
              {!formToken ? (
                <Button onClick={startPayment} disabled={loading} className="bg-red-600 hover:bg-red-700 w-full text-lg py-6" data-testid="start-payment-btn">
                  {loading
                    ? <><Loader2 className="animate-spin mr-2" size={20} /> Preparation...</>
                    : (PAYMENTS_ENABLED
                        ? <><CreditCard className="mr-2" size={20} /> Payer {getCartTotal().toFixed(2)} EUR</>
                        : <><CheckCircle className="mr-2" size={20} /> Confirmer ma commande ({getCartTotal().toFixed(2)} EUR a regler en magasin)</>)}
                </Button>
              ) : (
                <SystemPayForm
                  formToken={formToken}
                  amount={getCartTotal()}
                  onPaid={handlePaid}
                  onFailed={handleFailed}
                  testId="checkout-systempay-form"
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
